// ExplorerTabs — the shared tab strip above a folder explorer (Dashboards /
// Templates and Reports / Templates). Built on the repo's shadcn/Radix tabs
// primitives so it gets real tablist/tab roles + keyboard navigation for free;
// the styling is overridden to the compact, monospace explorer look so every
// explorer page's toggle matches. Presentational — Radix tracks the active tab
// via `value`/`onValueChange`; the wrapping page reads the same URL param to
// decide which sub-page to render, so there are no `TabsContent` panels here.

import type { LucideIcon } from 'lucide-react'

import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'

export interface ExplorerTab<T extends string> {
  id: T
  label: string
  icon: LucideIcon
}

export interface ExplorerTabsProps<T extends string> {
  tabs: readonly ExplorerTab<T>[]
  active: T
  onChange: (id: T) => void
  /** Each tab gets `data-testid={`${testIdPrefix}-${id}`}`. */
  testIdPrefix: string
  /** `role="tablist"` aria-label. */
  ariaLabel?: string
}

function ExplorerTabs<T extends string>({
  tabs,
  active,
  onChange,
  testIdPrefix,
  ariaLabel = 'Folder explorer view'
}: ExplorerTabsProps<T>) {
  return (
    <Tabs value={active} onValueChange={(value) => onChange(value as T)} className="gap-0">
      <TabsList
        aria-label={ariaLabel}
        variant="line"
        className="flex w-full justify-start gap-1 rounded-none border-b border-line bg-bg-elev px-2 py-1.5 group-data-[orientation=horizontal]/tabs:h-auto"
      >
        {tabs.map(({ id, label, icon: Icon }) => (
          <TabsTrigger
            key={id}
            value={id}
            data-testid={`${testIdPrefix}-${id}`}
            className="h-auto flex-none rounded border-0 px-2 py-1 font-mono text-12 font-normal text-ink-2 after:hidden hover:bg-bg-sunk/60 hover:text-ink data-[state=active]:bg-brand-soft data-[state=active]:text-brand data-[state=active]:shadow-none dark:text-ink-2 dark:hover:text-ink"
          >
            <Icon className="size-3" aria-hidden />
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}

export default ExplorerTabs
